import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

const PAD_PREFIX = 'fox-paid-dates-carousel';
const padVar = `--${PAD_PREFIX}-pad`;
const padAfterVar = `--${PAD_PREFIX}-pad-after`;

/**
 * Fox-Paid date ideas carousel. Desktop (fine pointer) mirrors the WEEKLY FEATURED
 * carousel (`RegionalFeatureCarousel` in HomePage.js): native horizontal scroll, per-frame
 * depth scale/opacity on the card inners (layer-promoted in CSS, so the writes stay
 * compositor-only), scrollend + debounced-scroll snap nudge, and a rAF loop through
 * scroll-snap deceleration.
 *
 * Touch devices intentionally DIVERGE from the weekly carousel — this strip wedges iOS
 * gesture snapping (refuses to pan off the current card, while programmatic dot scrolls
 * keep working) unless:
 *  - no inline transform/opacity/will-change exists anywhere inside the snap slides, and
 *  - scrollLeft is never written near a native snap (only when genuinely stuck mid-track,
 *    never while a finger is down or momentum/snap animation is in flight).
 * So on coarse pointers depth is disabled entirely and the snap nudge uses a large stuck
 * threshold. Don't "unify" these paths again without testing on a real iPhone.
 *
 * That second rule applies to EVERY writer, not just the post-gesture nudge. The ResizeObserver
 * re-centers as well, and because this page mutates continuously, its resizes land mid-swipe often
 * enough to look like a broken carousel: padding and scrollLeft get rewritten under the finger,
 * momentum dies, and the strip returns to the card it started on. `touchActiveRef` /
 * `lastScrollAtRef` are hook-scoped so new callers inherit the guard instead of re-learning this.
 */
export function useFiniteSnapCarousel(items, maxCount = 5) {
  const displayItems = useMemo(() => (items || []).slice(0, maxCount), [items, maxCount]);
  const displayCount = displayItems.length;

  const carouselRef = useRef(null);
  const cardRefs = useRef([]);
  /** Smoothed scale/opacity per card — eases jerk from sparse scroll events + scroll-snap jumps. */
  const depthSmoothRef = useRef([]);
  const activeIndexRef = useRef(0);
  /** iOS: smooth programmatic scroll + scroll-snap-stop:always often freezes mid-track — use instant scroll on coarse pointers (same guard as the WEEKLY FEATURED carousel). */
  const smoothScrollOkRef = useRef(true);
  /** Coarse pointers get no depth writes — inline styles on the slides' subtree wedge iOS gesture snapping (see applyDepth). */
  const depthEnabledRef = useRef(true);
  const depthClearedRef = useRef(false);
  /**
   * Gesture state, shared by every code path that writes scrollLeft or padding. It has to live at
   * hook scope rather than inside one effect: the ResizeObserver re-centers too, and a write while
   * a finger is down (or while momentum is still running) aborts Safari's touch tracking — the
   * strip snaps back to the card it started on and reads as frozen.
   */
  const touchActiveRef = useRef(false);
  const lastScrollAtRef = useRef(0);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const finePointer = !window.matchMedia('(pointer: coarse)').matches;
    smoothScrollOkRef.current = finePointer;
    depthEnabledRef.current = finePointer;
  }, []);

  /** Measured inline padding sync (no rAF deferral): WebKit can snap against layout before trailing space settles → first stop off-center, then jerky re-snap. Optional `reindexScrollIdx` re-sets scrollLeft after padding (mounted center / programmatic index). */
  const syncPadding = useCallback(
    (reindexScrollIdx = null) => {
      const el = carouselRef.current;
      if (!el) return;
      if (displayCount === 0) {
        el.style.removeProperty(padVar);
        el.style.removeProperty(padAfterVar);
        return;
      }
      const first = cardRefs.current[0];
      if (!first) return;
      const pad = Math.max(0, (el.clientWidth - first.offsetWidth) / 2);
      el.style.setProperty(padVar, `${pad}px`);

      let trailerPx = 0;
      el.style.setProperty(padAfterVar, '0px');

      const lastExtentDeficitPx = () => {
        const last = cardRefs.current[displayCount - 1];
        if (!last || displayCount < 2) return 0;
        void el.offsetHeight;
        const need = last.offsetLeft + last.offsetWidth / 2 - el.clientWidth / 2;
        const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
        return need - maxScroll;
      };

      for (let iter = 0; iter < 12; iter += 1) {
        void el.offsetHeight;
        const deficit = lastExtentDeficitPx();
        if (deficit <= 1) break;
        trailerPx += Math.max(1, Math.ceil(deficit));
        el.style.setProperty(padAfterVar, `${trailerPx}px`);
      }

      if (displayCount < 2) {
        el.style.setProperty(padAfterVar, '0px');
      }

      if (typeof reindexScrollIdx === 'number') {
        const card = cardRefs.current[reindexScrollIdx];
        if (card) {
          const raw = card.offsetLeft + card.offsetWidth / 2 - el.clientWidth / 2;
          const maxS = Math.max(0, el.scrollWidth - el.clientWidth);
          el.scrollLeft = Math.max(0, Math.min(maxS, Math.round(raw)));
        }
      }
    },
    [displayCount],
  );

  /** Depth: scale/opacity from each card's distance to the container's horizontal center (updates while scrolling). Values are exponentially smoothed so motion stays fluid between scroll samples and through scroll-snap. */
  const applyDepth = useCallback(() => {
    const container = carouselRef.current;
    if (!container) return;
    const cards = cardRefs.current.filter(Boolean);
    if (cards.length === 0) return;

    const containerCenter = container.scrollLeft + container.clientWidth / 2;
    const slotWidth = Math.max(1, cards[0].offsetWidth + 16); // ~card + gap; floor avoids div-by-zero
    let nearestIdx = 0;
    let nearestDist = Infinity;
    cards.forEach((card, i) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const d = Math.abs(cardCenter - containerCenter);
      if (d < nearestDist) {
        nearestDist = d;
        nearestIdx = i;
      }
    });
    if (activeIndexRef.current !== nearestIdx) {
      activeIndexRef.current = nearestIdx;
      setActiveIndex(nearestIdx);
    }

    /*
     * Coarse pointers: no depth writes at all. iOS gesture snapping wedges (the strip refuses to
     * pan off the current card, while programmatic dot scrolls still work) when the snap slides —
     * or their composited children — carry inline transforms/will-change during native scroll.
     * This is the empirically known-scrolling configuration; keep the inners style-free on touch.
     */
    if (!depthEnabledRef.current) {
      if (!depthClearedRef.current) {
        depthClearedRef.current = true;
        cards.forEach((card) => {
          const inner = card.firstElementChild;
          if (inner) {
            inner.style.removeProperty('transform');
            inner.style.removeProperty('opacity');
            inner.style.removeProperty('z-index');
          }
        });
      }
      return;
    }
    depthClearedRef.current = false;

    const smoothArr = depthSmoothRef.current;
    if (smoothArr.length !== cards.length) {
      depthSmoothRef.current = cards.map(() => null);
    }
    const smoothed = depthSmoothRef.current;

    cards.forEach((card, i) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const rawDist = Math.abs(cardCenter - containerCenter);
      const dist = rawDist / slotWidth;
      const targetScale = Math.max(0.6, 1 - dist * 0.22);
      const targetOpacity = Math.max(0.55, 1 - dist * 0.22);

      let sm = smoothed[i];
      if (sm == null) {
        sm = { scale: targetScale, opacity: targetOpacity };
        smoothed[i] = sm;
      } else {
        /* Lower blend = gentler easing (more frames to converge); big-gap branch still catches snap jumps. */
        const blendScale = Math.abs(targetScale - sm.scale) > 0.08 ? 0.45 : 0.22;
        const blendOpacity = Math.abs(targetOpacity - sm.opacity) > 0.08 ? 0.45 : 0.22;
        sm.scale += (targetScale - sm.scale) * blendScale;
        sm.opacity += (targetOpacity - sm.opacity) * blendOpacity;
        if (Math.abs(targetScale - sm.scale) < 0.002) sm.scale = targetScale;
        if (Math.abs(targetOpacity - sm.opacity) < 0.002) sm.opacity = targetOpacity;
      }

      /* Write to the inner shell, never the slide: the slide is the scroll-snap target and iOS
         gesture snapping wedges (can't swipe off the current card) when the snap target carries
         transform/will-change. The inner is layer-promoted in CSS, so these writes stay compositor-only. */
      const inner = card.firstElementChild;
      if (inner) {
        inner.style.transform = `translateZ(0) scale(${sm.scale.toFixed(4)})`;
        inner.style.opacity = sm.opacity.toFixed(4);
        inner.style.zIndex = String(Math.round(100 - dist * 10));
      }
    });
  }, []);

  /** Dot / side-card click: smooth-scroll the chosen card to strip center (same centering math as the snap nudge). */
  const scrollToIndex = useCallback((idx) => {
    const container = carouselRef.current;
    const card = cardRefs.current[idx];
    if (!container || !card) return;
    const raw = card.offsetLeft + card.offsetWidth / 2 - container.clientWidth / 2;
    const maxS = Math.max(0, container.scrollWidth - container.clientWidth);
    const exact = Math.max(0, Math.min(maxS, Math.round(raw)));
    if (smoothScrollOkRef.current) {
      container.scrollTo({ left: exact, behavior: 'smooth' });
    } else {
      container.scrollLeft = exact;
    }
  }, []);

  /** After scroll-snap / momentum ends, Safari can rest a few px off computed center (esp. last card); exact scroll fixes without a second gesture. */
  const nudgeSnap = useCallback(() => {
    const container = carouselRef.current;
    if (!container || displayCount < 1) return;
    /* Universal guard: correcting under an active finger is what wedges the strip, and callers
       have historically forgotten to check (see the ResizeObserver). */
    if (touchActiveRef.current) return;
    const cards = cardRefs.current.filter(Boolean);
    if (cards.length === 0) return;
    const midpoint = container.scrollLeft + container.clientWidth / 2;
    let best = 0;
    let bestD = Infinity;
    cards.forEach((c, i) => {
      const cx = c.offsetLeft + c.offsetWidth / 2;
      const d = Math.abs(cx - midpoint);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    const card = cards[best];
    const raw = card.offsetLeft + card.offsetWidth / 2 - container.clientWidth / 2;
    const maxS = Math.max(0, container.scrollWidth - container.clientWidth);
    const exact = Math.max(0, Math.min(maxS, Math.round(raw)));
    const delta = Math.abs(container.scrollLeft - exact);
    if (!smoothScrollOkRef.current) {
      /*
       * Touch: Safari's native snap can rest a few px off our computed center (fractional
       * widths / gap / padding rounding). Correcting those tiny offsets puts us in a silent
       * tug-of-war with the native snap engine (it re-snaps, we re-correct, forever) that eats
       * pan gestures — only intervene when the strip is genuinely stuck between snap points,
       * and jump instantly (smooth scroll can freeze mid-track on iOS).
       */
      const stuckThreshold = Math.max(16, card.offsetWidth * 0.2);
      if (delta > stuckThreshold) container.scrollLeft = exact;
      return;
    }
    if (delta <= 1) return;
    if (delta <= 4) {
      /* Sub-4px correction is imperceptible — instant set avoids kicking off a scroll animation. */
      container.scrollLeft = exact;
    } else {
      container.scrollTo({ left: exact, behavior: 'smooth' });
    }
  }, [displayCount]);

  /** Start centered on the middle card when the list is known (finite strip — no copy wrap). */
  useLayoutEffect(() => {
    const container = carouselRef.current;
    if (!container) return;
    if (displayCount === 0) {
      container.style.removeProperty(padVar);
      container.style.removeProperty(padAfterVar);
      return;
    }
    cardRefs.current.length = displayItems.length;
    depthSmoothRef.current = [];
    const centerIdx = Math.floor(displayCount / 2);
    syncPadding(centerIdx);
    applyDepth();
    requestAnimationFrame(() => {
      nudgeSnap();
      applyDepth();
    });
  }, [displayItems, displayCount, syncPadding, applyDepth, nudgeSnap]);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let roRaf = null;
    let retryId = null;

    const resync = () => {
      roRaf = null;
      retryId = null;
      /*
       * The page mutates continuously, so resizes routinely land mid-swipe. Re-centering then
       * rewrites padding and scrollLeft under the finger, which cancels iOS momentum and pins the
       * strip to its starting card. Wait for rest instead — a resize is never urgent.
       */
      if (touchActiveRef.current || performance.now() - lastScrollAtRef.current < 250) {
        retryId = window.setTimeout(resync, 250);
        return;
      }
      syncPadding();
      nudgeSnap();
      applyDepth();
    };

    const ro = new ResizeObserver(() => {
      /* Defer to next frame: style/layout writes here re-enter ResizeObserver and throw in some browsers. */
      if (roRaf !== null || retryId !== null) return;
      roRaf = requestAnimationFrame(resync);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (roRaf !== null) cancelAnimationFrame(roRaf);
      if (retryId !== null) window.clearTimeout(retryId);
    };
  }, [syncPadding, nudgeSnap, applyDepth]);

  /**
   * Post-gesture (`scrollend` + debounced `scroll` fallback): WebKit lands mandatory snap shy of
   * analytical center unless we clamp `scrollLeft`.
   * iOS guards: never nudge while a finger is down (writing scrollLeft mid-drag aborts Safari's
   * touch tracking — the strip then feels frozen), and confirm across a frame that momentum/snap
   * animation has truly stopped before writing (a write mid-snap fights the native animation).
   */
  useEffect(() => {
    const container = carouselRef.current;
    if (!container || displayCount < 1) return;

    let debounceId = null;
    let rafId = null;

    const cancelPending = () => {
      if (debounceId !== null) {
        window.clearTimeout(debounceId);
        debounceId = null;
      }
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    const armDebounce = () => {
      if (debounceId !== null) window.clearTimeout(debounceId);
      debounceId = window.setTimeout(() => {
        debounceId = null;
        nudgeQuiet();
      }, 120);
    };

    const nudgeQuiet = () => {
      if (touchActiveRef.current) return;
      const leftBefore = container.scrollLeft;
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (touchActiveRef.current) return;
        /* Still moving — momentum or the native snap animation is in flight; check back later. */
        if (container.scrollLeft !== leftBefore) {
          armDebounce();
          return;
        }
        nudgeSnap();
        applyDepth();
      });
    };

    const onScroll = () => {
      lastScrollAtRef.current = performance.now();
      if (!touchActiveRef.current) armDebounce();
    };

    const onTouchStart = () => {
      touchActiveRef.current = true;
      cancelPending();
    };

    const onTouchEnd = () => {
      touchActiveRef.current = false;
      /* No momentum after release means no more scroll events — still verify the resting spot. */
      armDebounce();
    };

    container.addEventListener('scrollend', nudgeQuiet);
    container.addEventListener('scroll', onScroll, { passive: true });
    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('touchend', onTouchEnd, { passive: true });
    container.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      container.removeEventListener('scrollend', nudgeQuiet);
      container.removeEventListener('scroll', onScroll);
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchEnd);
      cancelPending();
    };
  }, [displayCount, nudgeSnap, applyDepth]);

  /** Keep depth in sync during scroll-snap animation (scroll events can be sparse). `displayCount` dep matters: the component renders nothing until items load, so this must re-run once the track exists. */
  useEffect(() => {
    const container = carouselRef.current;
    if (!container || displayCount < 1) return;
    let rafId = null;
    let lastScrollLeft = container.scrollLeft;
    let stableFrames = 0;

    const tick = () => {
      const cur = container.scrollLeft;
      if (cur === lastScrollLeft) {
        stableFrames += 1;
      } else {
        stableFrames = 0;
        lastScrollLeft = cur;
      }
      applyDepth();
      if (stableFrames < 22) {
        rafId = requestAnimationFrame(tick);
      } else {
        rafId = null;
      }
    };
    const schedule = () => {
      if (rafId !== null) return;
      stableFrames = 0;
      lastScrollLeft = container.scrollLeft;
      rafId = requestAnimationFrame(tick);
    };
    /* Resize is covered by the ResizeObserver effect above (container size changes re-run padding sync + depth). */
    container.addEventListener('scroll', schedule, { passive: true });
    return () => {
      container.removeEventListener('scroll', schedule);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [displayCount, applyDepth]);

  return {
    carouselRef,
    cardRefs,
    activeIndex,
    scrollToIndex,
    displayItems,
    displayCount,
  };
}
